import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import './DailyActivity.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ACTIVITIES = [
  { key: 'sale',              label: 'Sales',           pts: 20 },
  { key: 'appt_attended',     label: 'Appts Attended',  pts: 10 },
  { key: 'motis_appointment', label: 'MOTIS Appts',     pts: 10 },
  { key: 'appt_set',          label: 'Appts Set',       pts: 10 },
  { key: 'ac_partner',        label: 'AC Partner',      pts: 5  },
  { key: 'networking_event',  label: 'Networking',      pts: 5  },
  { key: 'knock',             label: 'Knocks',          pts: 2  },
  { key: 'opp_created',       label: 'Opps Created',    pts: 2  },
  { key: 'call',              label: 'Calls',           pts: 1  },
  { key: 'email',             label: 'Emails',          pts: 1  },
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const rep = payload[0].payload;
  const active_activities = ACTIVITIES.filter(a => rep[a.key] > 0);
  return (
    <div className="da-tooltip">
      <div className="da-tooltip-name">{rep.display_name}</div>
      <div className="da-tooltip-total">{rep.total_points} pts today</div>
      {active_activities.length > 0 ? (
        active_activities.map(a => (
          <div key={a.key} className="da-tooltip-row">
            <span className="da-tooltip-act">{a.label}</span>
            <span className="da-tooltip-val">
              {rep[a.key]}
              <span className="da-tooltip-pts"> ({rep[a.key] * a.pts} pts)</span>
            </span>
          </div>
        ))
      ) : (
        <div className="da-tooltip-empty">No activity logged yet</div>
      )}
    </div>
  );
};

const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DailyActivity = ({ onBack, user, managerId, managerName }) => {
  const [selectedDate, setSelectedDate] = useState(() => toLocalISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [colors, setColors] = useState({
    accent: '#5b8def',
    muted: '#8a90a0',
    chartText: '#b0b5c0',
    chartGrid: '#3d4455',
  });

  useEffect(() => {
    const style = getComputedStyle(document.documentElement);
    const get = (v) => style.getPropertyValue(v).trim();
    setColors({
      accent: get('--accent') || '#5b8def',
      muted: get('--text-muted') || '#8a90a0',
      chartText: get('--chart-text') || '#b0b5c0',
      chartGrid: get('--chart-grid') || '#3d4455',
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_URL}/api/admin/team-today/?date=${selectedDate}`;
      if (managerId) url += `&for_user_id=${managerId}`;
      const res = await fetch(url, {
        headers: { 'X-User-ID': String(user.userId) },
      });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch {
      setError('Failed to load team activity.');
    } finally {
      setLoading(false);
    }
  }, [user.userId, selectedDate, managerId]);

  useEffect(() => { load(); }, [load]);

  const todayISO = toLocalISO(new Date());
  const isToday = selectedDate === todayISO;

  const shiftDay = (delta) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const next = new Date(y, m - 1, d + delta);
    setSelectedDate(toLocalISO(next));
  };

  const team = (data?.team || []).map(r => ({
    ...r,
    display_name: r.full_name || r.username,
  }));

  const totalPts = team.reduce((s, r) => s + r.total_points, 0);
  const activeCount = team.filter(r => r.total_points > 0).length;
  const topRep = team[0];

  const todayLabel = new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const BAR_H = 46;
  const chartHeight = Math.max(team.length * BAR_H + 40, 100);

  if (loading) return (
    <div className="da-container"><div className="da-loading">Loading...</div></div>
  );

  const backLabel = managerId ? '← Back' : '← Back to Menu';
  const title = managerName ? `${managerName}'s Team` : 'Daily Activity';

  if (error) return (
    <div className="da-container">
      <button onClick={onBack} className="da-back">{backLabel}</button>
      <div className="da-error">{error}</div>
    </div>
  );

  return (
    <div className="da-container">
      <div className="da-header">
        <button onClick={onBack} className="da-back">{backLabel}</button>
        <div className="da-title-block">
          <h2>{title}</h2>
          <div className="da-nav">
            <button className="da-nav-btn" onClick={() => shiftDay(-1)}>◀</button>
            <span className="da-date">{todayLabel}</span>
            <button className="da-nav-btn" onClick={() => shiftDay(1)} disabled={isToday}>▶</button>
          </div>
        </div>
        <button onClick={load} className="da-refresh" title="Refresh">↻ Refresh</button>
      </div>

      <div className="da-stat-row">
        <div className="da-stat">
          <span className="da-stat-value">{totalPts}</span>
          <span className="da-stat-label">Team Points</span>
        </div>
        <div className="da-stat">
          <span className="da-stat-value">
            {activeCount}
            <span className="da-stat-denom"> / {team.length}</span>
          </span>
          <span className="da-stat-label">Active Today</span>
        </div>
        <div className="da-stat">
          {topRep && topRep.total_points > 0 ? (
            <>
              <span className="da-stat-value da-stat-name">{topRep.display_name}</span>
              <span className="da-stat-label">Top Rep · {topRep.total_points} pts</span>
            </>
          ) : (
            <>
              <span className="da-stat-value">—</span>
              <span className="da-stat-label">Top Rep</span>
            </>
          )}
        </div>
      </div>

      {team.length === 0 ? (
        <div className="da-empty">No reps on your team yet.</div>
      ) : (
        <div className="da-chart-wrap">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              layout="vertical"
              data={team}
              margin={{ top: 4, right: 64, left: 8, bottom: 4 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke={colors.chartGrid}
              />
              <XAxis
                type="number"
                tick={{ fill: colors.chartText, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="display_name"
                width={140}
                tick={{ fill: colors.chartText, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Bar dataKey="total_points" radius={[0, 4, 4, 0]} maxBarSize={30}>
                {team.map((rep, i) => (
                  <Cell
                    key={i}
                    fill={rep.total_points > 0 ? colors.accent : colors.muted}
                    fillOpacity={rep.total_points > 0 ? 1 : 0.3}
                  />
                ))}
                <LabelList
                  dataKey="total_points"
                  position="right"
                  style={{ fill: colors.chartText, fontSize: 12, fontWeight: 600 }}
                  formatter={(v) => v > 0 ? `${v}` : '—'}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default DailyActivity;
